// frontend/src/pages/staff/StaffServices.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import "./staffStyles.css";

function StaffServices() {
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Service templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [shopId, setShopId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine role type from path
  const getRoleType = useCallback(() => {
    return "professional";
  }, []);

  // Use unified professional dashboard path
  const basePath = "/dashboard/professional";

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const res = await axios.get(`${baseURL}/api/staff/services`, { headers });

      const fetchedServices = res.data.services || [];
      setServices(fetchedServices);
      // ALWAYS fetch profile to get businessId (services don't have businessId, only Staff profile does)
      try {
        const profileRes = await axios.get(`${baseURL}/api/staff/profile`, {
          headers,
        });
        const profile = profileRes.data.staff || profileRes.data;

        if (profile) {
          // Extract businessId for business-wide template access
          const extractedBusinessId =
            typeof profile.businessId === "object"
              ? profile.businessId?._id
              : profile.businessId;
          setBusinessId(extractedBusinessId);

          // Extract shopId for backward compatibility
          const extractedShopId =
            typeof profile.shopId === "object"
              ? profile.shopId?._id
              : profile.shopId;
          setShopId(extractedShopId);

          if (!extractedBusinessId) {
            // Fallback: Try to get businessId from shops (for business owners without staff profile)
            try {
              const shopsRes = await axios.get(`${baseURL}/api/shop/my-shops`, {
                headers,
              });
              if (shopsRes.data.shops && shopsRes.data.shops.length > 0) {
                const firstShopBusinessId = shopsRes.data.shops[0].businessId;
                const fallbackBusinessId =
                  typeof firstShopBusinessId === "object"
                    ? firstShopBusinessId?._id
                    : firstShopBusinessId;
                setBusinessId(fallbackBusinessId);
              }
            } catch (shopsErr) {
            }
          }
        } else {
          // No staff profile (owner without professional role) - get businessId from shops
          const shopsRes = await axios.get(`${baseURL}/api/shop/my-shops`, {
            headers,
          });
          if (shopsRes.data.shops && shopsRes.data.shops.length > 0) {
            const firstShopBusinessId = shopsRes.data.shops[0].businessId;
            const fallbackBusinessId =
              typeof firstShopBusinessId === "object"
                ? firstShopBusinessId?._id
                : firstShopBusinessId;
            setBusinessId(fallbackBusinessId);
          }
        }
      } catch (profileErr) {
        // Profile endpoint failed (owner without staff profile) - get businessId from shops
        try {
          const shopsRes = await axios.get(`${baseURL}/api/shop/my-shops`, {
            headers,
          });
          if (shopsRes.data.shops && shopsRes.data.shops.length > 0) {
            const firstShopBusinessId = shopsRes.data.shops[0].businessId;
            const fallbackBusinessId =
              typeof firstShopBusinessId === "object"
                ? firstShopBusinessId?._id
                : firstShopBusinessId;
            setBusinessId(fallbackBusinessId);
          }
        } catch (shopsErr) {
        }
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg("Unable to load services. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Fetch service templates from business (all locations)
  const fetchTemplates = useCallback(async () => {
    if (!businessId) {
      return;
    }
    setLoadingTemplates(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        return;
      }

      const url = `${baseURL}/api/services/templates/business/${businessId}?activeOnly=true`;
      const res = await axios.get(url, { headers });
      setTemplates(res.data || []);

      if (res.data && res.data.length > 0) {
      } else {
      }
    } catch (err) {
      setErrorMsg("Unable to load service templates");
    } finally {
      setLoadingTemplates(false);
    }
  }, [businessId, getAuthHeaders, getApiBaseUrl]);

  // Load templates when toggled or when businessId becomes available
  useEffect(() => {
    if (showTemplates && templates.length === 0 && businessId) {
      fetchTemplates();
    } else if (showTemplates && !businessId) {
    } else if (showTemplates && templates.length > 0) {
    }
  }, [showTemplates, templates.length, businessId, fetchTemplates]);

  // Toggle template selection
  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates((prev) => {
      if (prev.includes(templateId)) {
        return prev.filter((id) => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Add all selected templates at once
  const handleAddSelectedTemplates = async () => {
    if (selectedTemplates.length === 0) {
      alert("Please select at least one template");
      return;
    }

    const headers = getAuthHeaders();
    if (!headers) return;

    try {
      const templatesToAdd = templates.filter((t) =>
        selectedTemplates.includes(t._id),
      );
      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const skippedServices = [];

      for (const template of templatesToAdd) {
        // Check for duplicates by templateId
        const alreadyUsed = services.some(
          (service) => service.templateId === template._id,
        );

        if (alreadyUsed) {
          skippedCount++;
          skippedServices.push(template.name);
          continue;
        }

        try {
          const res = await axios.post(
            `${getApiBaseUrl()}/api/staff/services`,
            {
              name: template.name,
              description: template.description || "",
              price: template.price,
              durationMinutes: template.durationMinutes,
              category: template.category,
              serviceImage: template.serviceImage || "",
              templateId: template._id,
              isActive: true,
              // Carry the WHOLE pricing model, not just the per-unit half. This previously copied
              // `pricingType` but none of the fields that make the type usable, so a range template
              // arrived as a service of type "range" with NO options (and therefore no price), and an
              // hourly template lost its rate. The copy looked complete and was not.
              pricingType: template.pricingType || "fixed",
              pricePerUnit: template.pricePerUnit,
              perUnitMinutes: template.perUnitMinutes,
              unitLabel: template.unitLabel,
              maxUnitsPerBooking: template.maxUnitsPerBooking,
              hourlyRate: template.hourlyRate,
              maxHoursPerBooking: template.maxHoursPerBooking,
              maxPrice: template.maxPrice,
              priceTiers: template.priceTiers,
            },
            { headers },
          );

          if (res.status === 201) {
            successCount++;
          }
        } catch (err) {
          errorCount++;
        }
      }

      // Build result message
      let message = "";
      if (successCount > 0) {
        message += `Successfully added ${successCount} service(s).`;
      }
      if (skippedCount > 0) {
        message += `\n${skippedCount} service(s) skipped (already added): ${skippedServices.join(", ")}`;
      }
      if (errorCount > 0) {
        message += `\n${errorCount} service(s) failed to add.`;
      }

      if (successCount > 0 || skippedCount > 0) {
        alert(message || "Operation completed.");
        setSelectedTemplates([]);
        fetchServices();
      } else {
        alert("Failed to add any services. Please try again.");
      }
    } catch (err) {
      alert("An error occurred while adding templates");
    }
  };

  // Use a template to create a new service
  const handleUseTemplate = async (template) => {
    try {
      // Check if this template has already been used by checking templateId
      const alreadyUsed = services.some(
        (service) => service.templateId === template._id,
      );

      if (alreadyUsed) {
        alert(
          `You've already added the "${template.name}" template. Please edit your existing service instead.`,
        );
        return;
      }

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Templates are business-level, services are staff-level
      // Backend automatically associates service with staffId from auth token
      const newService = {
        name: template.name,
        description: template.description,
        price: template.price,
        durationMinutes: template.durationMinutes,
        category: template.category,
        serviceImage: template.serviceImage,
        templateId: template._id,
        isActive: true,
        // Carry the WHOLE pricing model — see the note on the bulk-add payload above. Both copy sites
        // had the same gap, so fixing only one would have left the other still dropping the options.
        pricingType: template.pricingType || "fixed",
        pricePerUnit: template.pricePerUnit,
        perUnitMinutes: template.perUnitMinutes,
        unitLabel: template.unitLabel,
        maxUnitsPerBooking: template.maxUnitsPerBooking,
        hourlyRate: template.hourlyRate,
        maxHoursPerBooking: template.maxHoursPerBooking,
        maxPrice: template.maxPrice,
        priceTiers: template.priceTiers,
      };
      const res = await axios.post(
        `${baseURL}/api/staff/services`,
        newService,
        { headers },
      );

      if (res.status === 201) {
        alert(`Service "${template.name}" added successfully!`);

        // Force immediate refresh of services list
        await fetchServices();

        // Close templates section
        setShowTemplates(false);
      }
    } catch (err) {
      setErrorMsg("Unable to create service from template");
    }
  };

  const handleToggleActive = async (serviceId, currentStatus) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/services/${serviceId}`,
        { isActive: !currentStatus },
        { headers },
      );

      // Update local state
      setServices(
        services.map((svc) =>
          svc.id === serviceId ? { ...svc, isActive: !currentStatus } : svc,
        ),
      );
    } catch (err) {
      setErrorMsg("Unable to update service status");
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.delete(`${baseURL}/api/staff/services/${serviceId}`, {
        headers,
      });

      // Remove from local state
      setServices(services.filter((svc) => svc.id !== serviceId));
    } catch (err) {
      setErrorMsg("Unable to delete service");
    }
  };

  // Filter services - show all services to professionals (active and deactivated)
  const filteredServices = services.filter((service) => {
    const matchesCategory = filter === "all" || service.category === filter;
    const matchesSearch =
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (service.description &&
        service.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Handle import services from CSV/Excel
  const handleImportServices = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!["csv", "xlsx", "xls"].includes(fileExtension)) {
      setErrorMsg("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }

    try {
      const text = await file.text();
      const lines = text.split("\n").filter((line) => line.trim());

      if (lines.length < 2) {
        setErrorMsg("File must contain a header row and at least one service");
        return;
      }

      // Parse CSV (simple parser - assumes comma-separated values)
      const csvHeaders = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const importedServices = [];

      // Expected columns: name, description, price, duration (or durationMinutes), category
      const nameIndex = csvHeaders.findIndex(
        (h) => h === "name" || h === "service name",
      );
      const descIndex = csvHeaders.findIndex(
        (h) => h === "description" || h === "desc",
      );
      const priceIndex = csvHeaders.findIndex((h) => h === "price");
      const durationIndex = csvHeaders.findIndex(
        (h) =>
          h === "duration" ||
          h === "durationminutes" ||
          h === "duration (minutes)",
      );
      const categoryIndex = csvHeaders.findIndex((h) => h === "category");

      if (nameIndex === -1 || priceIndex === -1 || durationIndex === -1) {
        setErrorMsg("CSV must include columns: name, price, and duration");
        return;
      }

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());

        const name = values[nameIndex];
        const price = parseFloat(values[priceIndex]);
        const duration = parseInt(values[durationIndex]);
        const description = descIndex !== -1 ? values[descIndex] : "";
        const category =
          categoryIndex !== -1
            ? values[categoryIndex]?.toLowerCase() || "other"
            : "other";

        if (name && !isNaN(price) && !isNaN(duration)) {
          importedServices.push({
            name,
            description,
            price,
            durationMinutes: duration,
            category: [
              "haircut",
              "color",
              "styling",
              "treatment",
              "spa",
              "nails",
              "makeup",
              "waxing",
              "other",
            ].includes(category)
              ? category
              : "other",
            isActive: true,
          });
        }
      }

      if (importedServices.length === 0) {
        setErrorMsg("No valid services found in the file");
        return;
      }

      // Send to backend
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.post(
        `${baseURL}/api/staff/services/bulk`,
        {
          services: importedServices,
        },
        { headers },
      );

      if (response.status === 201) {
        alert(
          `Successfully imported ${importedServices.length} service(s)! You can now edit them to add images.`,
        );
        fetchServices(); // Refresh list
        e.target.value = ""; // Reset file input
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          "Failed to import services. Please check the file format.",
      );
    }
  };

  const roleType = getRoleType();

  const categories = [
    { value: "all", label: "All" },
    { value: "haircut", label: "Haircut" },
    { value: "color", label: "Color" },
    { value: "styling", label: "Styling" },
    { value: "treatment", label: "Treatment" },
    { value: "spa", label: "Spa" },
    { value: "nails", label: "Nails" },
    { value: "makeup", label: "Makeup" },
    { value: "waxing", label: "Waxing" },
    { value: "other", label: "Other" },
  ];

  if (loading) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading services...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div
        style={{
          padding: "20px",
          paddingBottom: window.innerWidth < 768 ? "180px" : "60px",
        }}
      >
        {/* Back to Dashboard Button */}
        <button
          onClick={() => navigate(basePath)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#f5f5f5";
            e.target.style.borderColor = "#001d4a";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#fff";
            e.target.style.borderColor = "#ddd";
          }}
        >
          <span>←</span> Back to Dashboard
        </button>

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "30px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <h2
            style={{
              margin: 0,
              width: window.innerWidth < 768 ? "100%" : "auto",
            }}
          >
            Services
          </h2>
          <div
            style={{
              display: window.innerWidth < 768 ? "grid" : "flex",
              gridTemplateColumns:
                window.innerWidth < 768 ? "repeat(2, 1fr)" : undefined,
              gap: window.innerWidth < 768 ? "8px" : "10px",
              columnGap: window.innerWidth < 768 ? "8px" : "10px",
              rowGap: window.innerWidth < 768 ? "8px" : "10px",
              width: window.innerWidth < 768 ? "100%" : "auto",
              justifyContent: window.innerWidth < 768 ? "stretch" : "flex-end",
            }}
          >
            <button
              className="btn-primary"
              style={{
                background: showTemplates ? "#6c757d" : "#001d4a",
                padding: window.innerWidth < 768 ? "8px 10px" : "10px 20px",
                fontSize: window.innerWidth < 768 ? "12px" : "14px",
                whiteSpace: "nowrap",
                minWidth: window.innerWidth < 768 ? "100%" : "180px",
                gridColumn: window.innerWidth < 768 ? "1 / -1" : undefined,
                width: window.innerWidth < 768 ? "100%" : "auto",
              }}
              onClick={() => setShowTemplates(!showTemplates)}
            >
              {showTemplates ? "Hide Templates" : "📋 Browse Templates"}
            </button>
            <button
              className="btn-primary"
              onClick={() => navigate(`${basePath}/services/add`)}
              style={{
                padding: window.innerWidth < 768 ? "10px" : undefined,
                fontSize: window.innerWidth < 768 ? "13px" : undefined,
                whiteSpace: "nowrap",
              }}
            >
              + Add
            </button>
            <button
              className="btn-primary"
              style={{
                background: "#6c757d",
                padding: window.innerWidth < 768 ? "10px" : undefined,
                fontSize: window.innerWidth < 768 ? "13px" : undefined,
                whiteSpace: "nowrap",
              }}
              onClick={() => navigate(`${basePath}/services/bulk-add`)}
            >
              + Bulk
            </button>
            {/* DEACTIVATED: R3 professionals should only use templates
            <label 
              htmlFor=\"import-csv-input\"
              className=\"btn-primary\" 
              style={{ 
                background: '#10b981',
                padding: window.innerWidth < 768 ? '10px' : undefined,
                fontSize: window.innerWidth < 768 ? '13px' : undefined,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
                margin: 0
              }}
            >
              📥 Import
              <input
                id=\"import-csv-input\"
                type=\"file\"
                accept=\".csv,.xlsx,.xls\"
                onChange={(e) => handleImportServices(e)}
                style={{ display: 'none' }}
              />
            </label>
            <a 
              href=\"/sample-services.csv\"
              download=\"sample-services.csv\"
              className=\"btn-primary\" 
              style={{ 
                background: '#6366f1',
                padding: window.innerWidth < 768 ? '10px' : undefined,
                fontSize: window.innerWidth < 768 ? '13px' : undefined,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                whiteSpace: 'nowrap',
                margin: 0
              }}
            >
              📄 CSV
            </a>
            */}
          </div>
        </div>

        {/* Info Banner */}
        <div
          style={{
            background: "#e6f3ff",
            border: "1px solid #0066cc",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "18px" }}>ℹ️</span>
          <p
            style={{
              margin: 0,
              color: "#003d82",
              fontSize: window.innerWidth < 768 ? "12px" : "14px",
            }}
          >
            <strong>Active services</strong> are visible to customers for
            booking. <strong>Deactivated services</strong> are only visible to
            you and shop owners, not customers.
          </p>
        </div>

        {/* Import Instructions */}
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #10b981",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "start",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "18px" }}>📥</span>
          <div>
            <p
              style={{
                margin: 0,
                color: "#065f46",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Import Services from CSV
            </p>
            <p style={{ margin: 0, color: "#047857", fontSize: "12px" }}>
              Upload a CSV file with columns:{" "}
              <strong>name, price, duration, description, category</strong>.
              Images can be added after import by editing each service.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Shop Service Templates Section */}
        {showTemplates && (
          <div className="dashboard-card" style={{ marginBottom: "30px" }}>
            <h3 style={{ marginTop: 0 }}>Shop Service Templates</h3>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              Select a template created by your shop to quickly add a service
              with pre-filled details.
            </p>

            {loadingTemplates ? (
              <p
                style={{ textAlign: "center", color: "#666", padding: "20px" }}
              >
                Loading templates...
              </p>
            ) : templates.length === 0 ? (
              <p
                style={{ textAlign: "center", color: "#666", padding: "20px" }}
              >
                No service templates available. Your shop owner can create
                templates for you to use.
              </p>
            ) : (
              <>
                {/* Bulk Action Button */}
                {selectedTemplates.length > 0 && (
                  <div
                    style={{
                      position: "sticky",
                      top: "80px",
                      zIndex: 10,
                      marginBottom: "20px",
                      padding: "16px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                    >
                      {selectedTemplates.length} template
                      {selectedTemplates.length > 1 ? "s" : ""} selected
                    </span>
                    <button
                      onClick={handleAddSelectedTemplates}
                      style={{
                        padding: "10px 24px",
                        background: "white",
                        color: "#2d6da3",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                      onMouseOver={(e) =>
                        (e.target.style.transform = "scale(1.05)")
                      }
                      onMouseOut={(e) =>
                        (e.target.style.transform = "scale(1)")
                      }
                    >
                      Add Selected to My Services
                    </button>
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    // Same fix as the shop page (2026-10-23): five per row squeezed the cards on tablets, where the
                    // width is real but narrower. Let the browser fit what fits; phones keep two.
                    gridTemplateColumns: isMobile
                      ? "repeat(2, 1fr)"
                      : "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: isMobile ? "12px" : "20px",
                  }}
                >
                  {templates.map((template) => (
                    <div
                      key={template._id}
                      style={{
                        position: "relative",
                        border: selectedTemplates.includes(template._id)
                          ? "3px solid #2d6da3"
                          : "2px solid #e0e0e0",
                        borderRadius: "12px",
                        padding: isMobile ? "12px" : "20px",
                        background: selectedTemplates.includes(template._id)
                          ? "#f0f4ff"
                          : "#fafafa",
                        transition: "all 0.2s",
                        boxShadow: selectedTemplates.includes(template._id)
                          ? "0 4px 12px rgba(102, 126, 234, 0.2)"
                          : "none",
                      }}
                    >
                      {/* Checkbox */}
                      <div
                        onClick={() => toggleTemplateSelection(template._id)}
                        style={{
                          position: "absolute",
                          top: "12px",
                          left: "12px",
                          width: "28px",
                          height: "28px",
                          borderRadius: "6px",
                          border: selectedTemplates.includes(template._id)
                            ? "2px solid #2d6da3"
                            : "2px solid #d1d5db",
                          background: selectedTemplates.includes(template._id)
                            ? "#2d6da3"
                            : "white",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s",
                          zIndex: 2,
                        }}
                      >
                        {selectedTemplates.includes(template._id) && (
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5 4L6 11.5L2.5 8"
                              stroke="white"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      {template.serviceImage && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginBottom: "16px",
                          }}
                        >
                          <div
                            style={{
                              width: isMobile ? "100px" : "180px",
                              height: isMobile ? "100px" : "180px",
                              borderRadius: "50%",
                              overflow: "hidden",
                              border: "4px solid #e5e7eb",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            }}
                          >
                            <img
                              src={
                                template.serviceImage.startsWith("http://") ||
                                template.serviceImage.startsWith("https://")
                                  ? template.serviceImage
                                  : `${process.env.REACT_APP_API_BASE_URL}${template.serviceImage}`
                              }
                              alt={template.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        </div>
                      )}
                      <div
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          background: "#e0e0e0",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          marginBottom: "10px",
                          textTransform: "capitalize",
                        }}
                      >
                        {template.category}
                      </div>
                      <h4 style={{ margin: "10px 0" }}>{template.name}</h4>
                      {template.description && (
                        <p
                          style={{
                            color: "#666",
                            fontSize: "14px",
                            margin: "8px 0",
                          }}
                        >
                          {template.description}
                        </p>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          margin: "15px 0",
                          padding: "10px 0",
                          borderTop: "1px solid #ddd",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            Price
                          </div>
                          <div
                            style={{
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#03543f",
                            }}
                          >
                            ${template.price}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            Duration
                          </div>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>
                            {template.durationMinutes} min
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (selectedTemplates.includes(template._id)) {
                            handleAddSelectedTemplates();
                          } else {
                            handleUseTemplate(template);
                          }
                        }}
                        className="btn-primary"
                        style={{
                          width: "100%",
                          background: selectedTemplates.includes(template._id)
                            ? "#2d6da3"
                            : undefined,
                        }}
                      >
                        {selectedTemplates.includes(template._id)
                          ? "Add to My Services"
                          : "Use This Template"}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            gap: "15px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search */}
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: "1",
              minWidth: "250px",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          />

          {/* Category Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Services Grid */}
        {filteredServices.length > 0 ? (
          <div
            style={{
              display: "grid",
              // And the services grid below it — same five-per-row squeeze (2026-10-23).
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fill, minmax(220px, 1fr))",
              gap: isMobile ? "12px" : "20px",
            }}
          >
            {filteredServices.map((service) => (
              <div
                key={service.id}
                className="dashboard-card"
                style={{
                  padding: isMobile ? "12px" : "20px",
                  position: "relative",
                  border: service.isActive
                    ? "1px solid #e5e7eb"
                    : "2px solid #fbbf24",
                  background: service.isActive ? "#fff" : "#fffbeb",
                }}
              >
                {/* Service Image */}
                {service.serviceImage &&
                  (() => {
                    const baseURL = getApiBaseUrl();
                    // Check if image URL is already absolute (Azure Blob) or relative (local)
                    const isAbsoluteUrl =
                      service.serviceImage.startsWith("http://") ||
                      service.serviceImage.startsWith("https://");
                    const imageUrl = isAbsoluteUrl
                      ? service.serviceImage
                      : baseURL
                        ? `${baseURL}${service.serviceImage}`
                        : "";
                    return imageUrl ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            width: isMobile ? "100px" : "180px",
                            height: isMobile ? "100px" : "180px",
                            borderRadius: "50%",
                            overflow: "hidden",
                            border: "4px solid #e5e7eb",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={service.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                            }}
                            onLoad={(e) => {
                            }}
                          />
                        </div>
                      </div>
                    ) : null;
                  })()}

                {/* Category and Template Badge */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginBottom: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 12px",
                      background: "#e0e0e0",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "500",
                      textTransform: "capitalize",
                    }}
                  >
                    {service.category}
                  </span>
                  {service.templateId && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 12px",
                        background:
                          "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                        color: "white",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      📋 From Template
                    </span>
                  )}
                  {/* Phase 5: Location context badge */}
                  {service.locationPricing &&
                    service.locationPricing.length > 0 && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 12px",
                          background: "#dbeafe",
                          color: "#1e40af",
                          border: "1px solid #93c5fd",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                      >
                        📍 {service.locationPricing.length} location
                        {service.locationPricing.length > 1 ? "s" : ""}
                      </span>
                    )}
                </div>

                {/* Service Details */}
                <h4 style={{ margin: "10px 0" }}>{service.name}</h4>
                {service.description && (
                  <p
                    style={{
                      margin: "8px 0",
                      color: "#666",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {service.description}
                  </p>
                )}

                {/* Price and Duration */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: "15px 0",
                    padding: "10px 0",
                    borderTop: "1px solid #eee",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Price</div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#03543f",
                      }}
                    >
                      ${service.price}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      Duration
                    </div>
                    <div style={{ fontSize: "16px", fontWeight: "500" }}>
                      {service.durationMinutes} min
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "6px 14px",
                      background: service.isActive ? "#dcfce7" : "#fed7aa",
                      color: service.isActive ? "#15803d" : "#c2410c",
                      border: service.isActive
                        ? "1px solid #86efac"
                        : "1px solid #fb923c",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {service.isActive ? "✓ Active" : "⚠ Deactivated"}
                  </span>
                  {!service.isActive && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#92400e",
                        marginTop: "8px",
                        fontStyle: "italic",
                      }}
                    >
                      Hidden from customers
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    onClick={() =>
                      navigate(`${basePath}/services/edit/${service.id}`)
                    }
                    style={{
                      flex: "1",
                      padding: "8px 16px",
                      border: "1px solid #001d4a",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#001d4a",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() =>
                      handleToggleActive(service.id, service.isActive)
                    }
                    style={{
                      flex: "1",
                      padding: "8px 16px",
                      border: "1px solid #6c757d",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#6c757d",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    {service.isActive ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    style={{
                      padding: "8px 16px",
                      border: "1px solid #c00",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#c00",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="dashboard-card"
            style={{ padding: "40px", textAlign: "center" }}
          >
            <p style={{ color: "#666", marginBottom: "20px" }}>
              {searchQuery || filter !== "all"
                ? "No services match your filters"
                : "No services added yet. Browse templates above or create your own services."}
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate(`${basePath}/services/add`)}
            >
              Add New Service
            </button>
          </div>
        )}
      </div>
    </StaffSidebarLayout>
  );
}

export default StaffServices;
